import { useState } from "react";
import type { SolutionFeedbackFormState } from "../../types/feedback/feedback";
import RatingField from "../../components/pages/dashboard/solutions/pages/feedback-form/form/fields/rating";
import ImpactField from "../../components/pages/dashboard/solutions/pages/feedback-form/form/fields/challenge";
import PriorityField from "../../components/pages/dashboard/solutions/pages/feedback-form/form/fields/importance";
import ExplorationField from "../../components/pages/dashboard/solutions/pages/feedback-form/form/fields/payment";
import NextStepField from "../../components/pages/dashboard/solutions/pages/feedback-form/form/fields/followup";
import RoleField from "../../components/pages/dashboard/solutions/pages/feedback-form/form/fields/additional-feedback";
import GradientButton from "../../components/common/custom-gradient-button";

interface Props {
  token: string;
  solutionName: string;
}

export default function PreviewFeedbackForm({ token, solutionName }: Props) {
  const [formState, setFormState] = useState<SolutionFeedbackFormState>({
    rating: null,
    impactResponse: "",
    challengeText: "",
    priorityLevel: "",
    buyingSignal: "",
    relativeImportance: "",
    whyNot: "",
    explorationStage: "",
    nextStep: "",
    roleRelation: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`/api/preview/feedback/${token}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          rating: formState.rating,
          impact_response: formState.impactResponse,
          challenge_text: formState.challengeText,
          priority_level: formState.priorityLevel,
          exploration_stage: formState.explorationStage,
          next_step: formState.nextStep,
          role_relation: formState.roleRelation,
        }),
      });
      if (!res.ok) throw new Error("Submission failed");
      setSubmitted(true);
    } catch {
      setError("Failed to submit feedback. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-12">
        <div className="text-4xl mb-4">✓</div>
        <h3 className="text-[#DDEBFF] text-xl font-semibold mb-2">Thank you for your feedback!</h3>
        <p className="text-[#6B96D6]">Your thoughts on {solutionName} have been recorded.</p>
      </div>
    );
  }

  return (
    <div>
      <h2 className="text-[#DDEBFF] text-2xl font-light mb-2 text-center">Share Your Feedback</h2>
      <p className="text-[#6B96D6] text-center mb-8 text-sm">
        Help us understand how {solutionName} could work for you
      </p>
      <form onSubmit={handleSubmit} className="flex flex-col gap-8">
        {error && (
          <div className="bg-red-500/10 border border-red-500/30 text-red-400 rounded-lg px-4 py-3 text-sm">
            {error}
          </div>
        )}
        <RatingField formState={formState} setFormState={setFormState} />
        <ImpactField formState={formState} setFormState={setFormState} />
        <PriorityField formState={formState} setFormState={setFormState} />
        <ExplorationField formState={formState} setFormState={setFormState} />
        <NextStepField formState={formState} setFormState={setFormState} />
        <RoleField formState={formState} setFormState={setFormState} />
        <GradientButton
          type="submit"
          disabled={isSubmitting}
          className={isSubmitting ? "opacity-50 cursor-not-allowed" : ""}
        >
          {isSubmitting ? "Submitting..." : "Submit Feedback"}
        </GradientButton>
      </form>
    </div>
  );
}
